import { z } from 'zod/v4';
import { type MultiCurrencyPrices } from '../common/index';

// ============================================
// Currency Schemas
// ============================================

export const CurrencySchema = z.object({
  code: z.string({ message: 'Код валюты обязателен' })
    .min(3, 'Код валюты должен быть 3 символа')
    .max(3, 'Код валюты должен быть 3 символа')
    .toUpperCase(),
  name: z.string({ message: 'Название обязательно' })
    .min(1, 'Название обязательно')
    .max(100, 'Название слишком длинное (максимум 100 символов)'),
  symbol: z.string({ message: 'Символ обязателен' })
    .min(1, 'Символ обязателен')
    .max(10, 'Символ слишком длинный'),
  decimalPlaces: z.number()
    .int('Количество знаков после запятой должно быть целым числом')
    .min(0, 'Количество знаков после запятой не может быть отрицательным')
    .max(8, 'Количество знаков после запятой не может быть больше 8')
    .default(2),
  isActive: z.boolean().default(true),
  order: z.number().int('Порядок должен быть целым числом').default(0),
});

export const CreateCurrencySchema = CurrencySchema;
export const UpdateCurrencySchema = CurrencySchema.partial().omit({ code: true });

// ============================================
// Exchange Rate Schemas
// ============================================

export const ExchangeRateSchema = z.object({
  id: z.number(),
  fromCurrency: z.string(),
  toCurrency: z.string(),
  rate: z.string(), // numeric stored as string
  provider: z.string(),
  fetchedAt: z.date(),
  createdAt: z.date(),
});

export const ExchangeRatesResponseSchema = z.object({
  baseCurrency: z.string(),
  rates: z.record(z.string(), z.number()),
  provider: z.string(),
  fetchedAt: z.date(),
});

// ============================================
// Price Schemas
// ============================================

// Use common schema instead of duplicating
// export const MultiCurrencyPricesSchema is imported from ../common/index

export const PriceConversionRequestSchema = z.object({
  amount: z.number({ message: 'Сумма обязательна' })
    .positive('Сумма должна быть положительной'),
  fromCurrency: z.string({ message: 'Исходная валюта обязательна' })
    .min(3, 'Код валюты должен быть 3 символа')
    .max(3, 'Код валюты должен быть 3 символа'),
  toCurrency: z.string({ message: 'Целевая валюта обязательна' })
    .min(3, 'Код валюты должен быть 3 символа')
    .max(3, 'Код валюты должен быть 3 символа'),
});

export const PriceConversionResponseSchema = z.object({
  originalAmount: z.number(),
  convertedAmount: z.number(),
  fromCurrency: z.string(),
  toCurrency: z.string(),
  rate: z.number(),
  provider: z.string(),
});

// ============================================
// Types
// ============================================

export type Currency = z.infer<typeof CurrencySchema>;
export type CreateCurrencyInput = z.infer<typeof CreateCurrencySchema>;
export type UpdateCurrencyInput = z.infer<typeof UpdateCurrencySchema>;
export type ExchangeRate = z.infer<typeof ExchangeRateSchema>;
export type ExchangeRatesResponse = z.infer<typeof ExchangeRatesResponseSchema>;
export type PriceConversionRequest = z.infer<typeof PriceConversionRequestSchema>;
export type PriceConversionResponse = z.infer<typeof PriceConversionResponseSchema>;

// Re-export MultiCurrencyPrices for convenience
export type { MultiCurrencyPrices };

// Provider types
export interface ExchangeRateProviderResult {
  baseCurrency: string;
  rates: Record<string, number>;
  provider: string;
  fetchedAt: Date;
}
